/**
 * ActiveSubscription — Paket C (premium-checkout-redesign)
 *
 * Hero-Karte für Premium-User mit aktivem Abo (Prototyp: subscription.html
 * .card-brand). Zeigt Planname + Status-Pill, Kontingent-Beschreibung,
 * Dual-Quota-Meter und die drei Hero-Stats (bezahlt bis / Tage verbleibend /
 * Restwert). Der Tarif-Wechsel selbst lebt jetzt in der eigenständigen
 * PlanChangeCalculator-Karte (Card "Tarif wechseln") — diese Komponente ist
 * reine Statusanzeige.
 *
 * "Restwert deines Abos" kommt aus derselben Vorschau (preview_upgrade), die
 * auch der Rechner für den in ihm gewählten Zielplan abruft (siehe
 * SubscriptionPage: EIN gemeinsamer Query-State für Hero + Rechner).
 */
import { useTranslation } from 'react-i18next'
import { useAccount } from '../settings/useAccount'
import { fmtPrice } from '../../lib/fmt'
import type { Subscription } from './useSubscription'
import type { Plan } from './useSubscriptionPlans'
import { planDisplayName } from '../premium/planDisplayName'
import { planQuotas } from '../premium/planQuotas'
import type { PreviewUpgradeResult } from '../premium/usePreviewUpgrade'
import { remainingValue, formatMoney } from '../premium/planMath'
import {
  SUBSCRIPTION_STATUS_CSS,
  SUBSCRIPTION_STATUS_CSS_FALLBACK,
  formatSubscriptionDate,
} from './subscriptionUtils'

type ActiveSubscriptionProps = {
  subscription: Subscription
  currentPlan: Plan | null
  preview: PreviewUpgradeResult | undefined
  previewFetching: boolean
}

function QuotaRow({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const unlimited = limit == null
  // P1-1: ein Limit unter dem tatsaechlichen Verbrauch ist unglaubwuerdig (z.B.
  // Legacy-Plan-ID ohne Kontingent-Eintrag im Backend) — niemals "2 / 1"
  // rendern. Statt eines erfundenen Nenners zeigen wir nur den echten
  // Verbrauch und blenden den (dann bedeutungslosen) Fuellbalken aus.
  const limitUnreliable = !unlimited && used > (limit as number)
  const showBar = !unlimited && !limitUnreliable
  const pct = !showBar ? 0 : (limit as number) <= 0 ? (used > 0 ? 100 : 0) : Math.min(100, (used / (limit as number)) * 100)
  const valueText = showBar ? `${used} / ${limit}` : `${used}`

  return (
    <div style={{ flex: 1, minWidth: 150 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <span style={{ color: 'rgba(255,255,255,0.72)' }}>{label}</span>
        <span className="mono">{valueText}</span>
      </div>
      <div
        className="meter"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={showBar ? Math.max(limit as number, used) : undefined}
        aria-valuenow={used}
        style={{ background: 'rgba(255,255,255,0.25)', marginTop: 4, height: 6, borderRadius: 999 }}
      >
        {showBar && (
          <div style={{ height: '100%', width: `${pct}%`, background: 'white', borderRadius: 999 }} />
        )}
      </div>
    </div>
  )
}

function quotaLabel(value: number | 'unlimited' | 'unknown'): string {
  return typeof value === 'number' ? String(value) : ''
}

function daysRemainingFallback(currentPeriodEnd: string): number {
  return Math.max(0, Math.ceil((new Date(currentPeriodEnd).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
}

export function ActiveSubscription({
  subscription,
  currentPlan,
  preview,
  previewFetching,
}: ActiveSubscriptionProps) {
  const { t } = useTranslation('subscription')
  const { account } = useAccount()
  const quota = account?.hotspot_quota

  const periodEnd = formatSubscriptionDate(subscription.current_period_end)

  const statusKey = subscription.cancel_at_period_end
    ? 'cancel_at_period_end'
    : subscription.status
  const statusCssClass =
    SUBSCRIPTION_STATUS_CSS[statusKey as keyof typeof SUBSCRIPTION_STATUS_CSS] ??
    SUBSCRIPTION_STATUS_CSS_FALLBACK
  const statusText = subscription.cancel_at_period_end
    ? t('hero.pillEndsOn', { date: periodEnd })
    : t(`status.${statusKey}`)

  const { payment_method: pm } = subscription

  const daysRemaining =
    preview?.ok && preview.days_remaining != null
      ? preview.days_remaining
      : daysRemainingFallback(subscription.current_period_end)
  const remainingValueAmount =
    preview?.ok && preview.proration_credit != null
      ? preview.proration_credit
      : currentPlan != null
        ? remainingValue(currentPlan.price_eur_monthly, daysRemaining)
        : null

  const planQuota = currentPlan ? planQuotas(currentPlan) : null

  return (
    <div className="card card-brand card-pad-lg" data-testid="active-subscription">
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ fontSize: 26, fontWeight: 600, margin: 0, color: 'white' }} data-testid="hero-plan-name">
              {currentPlan?.name ?? planDisplayName(subscription.plan_id) ?? subscription.plan_id}
            </h2>
            <span className={statusCssClass} data-testid="status-badge">
              {statusText}
            </span>
          </div>
          {planQuota && (
            <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="hero-quota-description">
              {t('hero.quotaDescription', { mobile: quotaLabel(planQuota.mobile), iot: quotaLabel(planQuota.iot) })}
            </p>
          )}
          <div className="muted" style={{ fontSize: 13, marginTop: 6 }} data-testid="hero-price-line">
            {currentPlan != null && (
              <>
                {t('hero.pricePrefix', {
                  price: fmtPrice(currentPlan.price_eur_monthly, currentPlan.currency),
                })}
              </>
            )}
            {subscription.auto_renew === true ? (
              <>
                {t('hero.renewsLabel')} <strong style={{ color: 'white' }}>{periodEnd}</strong>
              </>
            ) : (
              <>
                {t('hero.expiresLabel')} <strong style={{ color: 'white' }}>{periodEnd}</strong>{' '}
                {t('hero.expiresNoRenewSuffix')}
              </>
            )}
            {pm && (
              <span data-testid="payment-method">
                {' '}{t('hero.paymentSuffix', { brand: pm.brand.toUpperCase(), last4: pm.last4 })}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="hero-stat">
        <div className="hs">
          <b>{periodEnd}</b>
          <span>{t('hero.paidUntilLabel')}</span>
        </div>
        <div className="hs">
          <b>{Math.round(daysRemaining)}</b>
          <span>{t('hero.daysRemainingLabel')}</span>
        </div>
        <div className="hs">
          {previewFetching || remainingValueAmount == null ? (
            <b
              aria-busy="true"
              aria-label={t('hero.remainingValueLoading')}
              data-testid="hero-remaining-value-loading"
              style={{ opacity: 0.6 }}
            >
              …
            </b>
          ) : (
            <b data-testid="hero-remaining-value">{formatMoney(remainingValueAmount, currentPlan?.currency ?? 'usd')}</b>
          )}
          <span>{t('hero.remainingValueLabel')}</span>
        </div>
      </div>

      {quota?.mobile && quota?.iot && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 18, maxWidth: 420 }}>
          <QuotaRow label={t('hero.quota.mobile')} used={quota.mobile.used} limit={quota.mobile.limit} />
          <QuotaRow label={t('hero.quota.iot')} used={quota.iot.used} limit={quota.iot.limit} />
        </div>
      )}
    </div>
  )
}
